import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, CheckCircle2, Send, Globe, MessageSquare, Smartphone } from 'lucide-react';
import { useReveal } from '../../hooks/useReveal';
import { trackFormSubmit } from '../../lib/tracking';
import { EMPRESA } from '../../lib/legal';

// Formsubmit.co — servicio de reenvío de formularios sin backend.
// Al primer submit envía un email de confirmación al destinatario;
// una vez confirmado, los siguientes envíos llegan al mismo email.
// Doc: https://formsubmit.co
const FORMSUBMIT_ENDPOINT = `https://formsubmit.co/ajax/${EMPRESA.emailGeneral}`;

const MOTIVO_LABELS = {
  demo: 'Solicitud de Demo',
  reunion: 'Agendar reunión',
  piloto: 'Solicitar piloto',
  info: 'Más información',
};

const initial = {
  nombre: '',
  cargo: '',
  colegio: '',
  email: '',
  telefono: '',
  comuna: '',
  mensaje: '',
  motivo: 'demo',
  acepta_legal: false,
};

const Contacto = ({ onOpenLegal }) => {
  const a = useReveal();
  const b = useReveal();
  const [data, setData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState('');

  // Preselección "Solicitud de Demo" cuando el CTA de Header/Hero dispara el evento.
  useEffect(() => {
    const onDemoRequest = () => {
      setData((d) => ({ ...d, motivo: 'demo' }));
      setSubmitted(false);
    };
    window.addEventListener('preventi:demo-request', onDemoRequest);
    return () => window.removeEventListener('preventi:demo-request', onDemoRequest);
  }, []);

  const validate = () => {
    const e = {};
    if (!data.nombre.trim()) e.nombre = 'Indícanos tu nombre.';
    if (!data.colegio.trim()) e.colegio = 'Indícanos el establecimiento.';
    if (!data.email.trim()) e.email = 'Necesitamos un email de contacto.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = 'Formato de email no válido.';
    if (!data.mensaje.trim() || data.mensaje.trim().length < 10) e.mensaje = 'Cuéntanos un poco más (mínimo 10 caracteres).';
    if (!data.acepta_legal) e.acepta_legal = 'Debes aceptar la Política de Privacidad para continuar.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => {
    const value = ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value;
    setData((d) => ({ ...d, [k]: value }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError('');
    if (!validate()) return;
    setSending(true);
    trackFormSubmit('contacto', { motivo: data.motivo, has_telefono: !!data.telefono });
    try {
      const payload = {
        Nombre: data.nombre,
        Cargo: data.cargo,
        Colegio: data.colegio,
        Email: data.email,
        Telefono: data.telefono,
        Comuna: data.comuna,
        Motivo: MOTIVO_LABELS[data.motivo] || data.motivo,
        Mensaje: data.mensaje,
        _subject: `PreventiApp · ${MOTIVO_LABELS[data.motivo] || 'Contacto'} — ${data.colegio}`,
        _template: 'table',
        _captcha: 'false',
      };
      const r = await fetch(FORMSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === 'false') {
        throw new Error(j.message || 'No pudimos enviar tu mensaje. Intenta nuevamente.');
      }
      setSubmitted(true);
    } catch (e) {
      setServerError(e.message || 'Error de red. Reintenta en unos segundos.');
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setData(initial);
    setErrors({});
    setSubmitted(false);
    setServerError('');
  };

  return (
    <section id="contacto" data-testid="contacto-section" className="relative py-14 sm:py-20 bg-soft">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        {/* Info */}
        <div ref={a.ref} className="reveal lg:col-span-2">
          <div className="chip" data-testid="contacto-chip">Contacto</div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-deep tracking-tight leading-tight">
            Conversemos sobre una <span className="text-brand-violet">implementación piloto</span> en tu colegio.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">
            Cuéntanos brevemente sobre tu establecimiento y agendamos una reunión de presentación. Te
            acompañamos paso a paso.
          </p>

          <ul className="mt-8 space-y-4" data-testid="contacto-info">
            <li className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-2xl bg-brand-violet/10 text-brand-violet flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-muted">Solicita tu demostración</p>
                <a data-testid="contacto-email-demo" href={`mailto:${EMPRESA.emailGeneral}`} className="text-base font-semibold text-brand-deep hover:text-brand-violet">
                  {EMPRESA.emailGeneral}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-2xl bg-brand-violet/10 text-brand-violet flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-muted">Privacidad y datos personales</p>
                <a data-testid="contacto-email-general" href={`mailto:${EMPRESA.emailPrivacidad}`} className="text-base font-semibold text-brand-deep hover:text-brand-violet">
                  {EMPRESA.emailPrivacidad}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-2xl bg-brand-violet/10 text-brand-violet flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-muted">Teléfono</p>
                <a data-testid="contacto-telefono-fijo" href="tel:+56229736035" className="text-base font-semibold text-brand-deep hover:text-brand-violet">
                  +56 2 2973 6035
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-2xl bg-brand-violet/10 text-brand-violet flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-muted">Móvil / WhatsApp</p>
                <a data-testid="contacto-telefono" href="https://wa.me/56996099788" target="_blank" rel="noopener noreferrer" className="text-base font-semibold text-brand-deep hover:text-brand-violet">
                  +56 9 9609 9788
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-2xl bg-brand-violet/10 text-brand-violet flex items-center justify-center shrink-0">
                <Globe className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-muted">Sitio web</p>
                <span data-testid="contacto-web" className="text-base font-semibold text-brand-deep">www.preventiapp.com</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-2xl bg-brand-violet/10 text-brand-violet flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-muted">Oficina</p>
                <span data-testid="contacto-ciudad" className="text-base font-semibold text-brand-deep">Santiago, Chile</span>
              </div>
            </li>
          </ul>
        </div>

        {/* Form */}
        <div ref={b.ref} className="reveal lg:col-span-3" style={{ transitionDelay: '120ms' }}>
          <div className="card-soft p-6 sm:p-9">
            {submitted ? (
              <div data-testid="contacto-success" className="text-center py-10">
                <div className="w-16 h-16 rounded-2xl bg-[#E6F8F0] text-[#28B487] mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="mt-6 text-2xl font-bold text-brand-deep">¡Mensaje enviado!</h3>
                <p className="mt-3 text-base text-brand-muted max-w-md mx-auto leading-relaxed">
                  Gracias por tu interés en PreventiApp. Un especialista del equipo se pondrá en contacto contigo
                  en las próximas horas para coordinar una reunión.
                </p>
                <button
                  data-testid="contacto-reset"
                  onClick={reset}
                  className="mt-8 btn-ghost px-6 py-3 rounded-full font-semibold text-sm"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contacto-form" noValidate>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-violet">Solicita una reunión</p>
                <h3 className="mt-2 text-2xl font-bold text-brand-deep">Cuéntanos sobre tu colegio</h3>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <Field label="Nombre completo" error={errors.nombre}>
                    <input
                      data-testid="input-nombre"
                      value={data.nombre}
                      onChange={onChange('nombre')}
                      placeholder="Ej. María Pérez"
                      className={inputCls(errors.nombre)}
                    />
                  </Field>
                  <Field label="Cargo / rol">
                    <input
                      data-testid="input-cargo"
                      value={data.cargo}
                      onChange={onChange('cargo')}
                      placeholder="Encargada de convivencia"
                      className={inputCls()}
                    />
                  </Field>
                  <Field label="Establecimiento" error={errors.colegio}>
                    <input
                      data-testid="input-colegio"
                      value={data.colegio}
                      onChange={onChange('colegio')}
                      placeholder="Colegio / Liceo"
                      className={inputCls(errors.colegio)}
                    />
                  </Field>
                  <Field label="Comuna">
                    <input
                      data-testid="input-comuna"
                      value={data.comuna}
                      onChange={onChange('comuna')}
                      placeholder="Comuna / ciudad"
                      className={inputCls()}
                    />
                  </Field>
                  <Field label="Email" error={errors.email}>
                    <input
                      data-testid="input-email"
                      type="email"
                      value={data.email}
                      onChange={onChange('email')}
                      placeholder="nombre@colegio.cl"
                      className={inputCls(errors.email)}
                    />
                  </Field>
                  <Field label="Teléfono / WhatsApp">
                    <input
                      data-testid="input-telefono"
                      value={data.telefono}
                      onChange={onChange('telefono')}
                      placeholder="+56 9 ..."
                      className={inputCls()}
                    />
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label="Motivo de contacto">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="motivo-group">
                      {[
                        { v: 'demo', label: 'Solicitud de Demo' },
                        { v: 'reunion', label: 'Agendar reunión' },
                        { v: 'piloto', label: 'Solicitar piloto' },
                        { v: 'info', label: 'Más información' },
                      ].map((opt) => (
                        <label
                          key={opt.v}
                          data-testid={`motivo-${opt.v}`}
                          className={`cursor-pointer rounded-xl border px-4 py-3 text-sm font-semibold text-center transition-all ${
                            data.motivo === opt.v
                              ? 'border-brand-violet bg-brand-violet/10 text-brand-violet'
                              : 'border-brand text-brand-deep hover:border-brand-violet/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name="motivo"
                            value={opt.v}
                            checked={data.motivo === opt.v}
                            onChange={onChange('motivo')}
                            className="sr-only"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </Field>
                </div>

                <div className="mt-4">
                  <Field label="Mensaje" error={errors.mensaje}>
                    <textarea
                      data-testid="input-mensaje"
                      value={data.mensaje}
                      onChange={onChange('mensaje')}
                      rows={4}
                      placeholder="Cuéntanos brevemente sobre tu colegio y qué te gustaría conocer de PreventiApp."
                      className={inputCls(errors.mensaje) + ' resize-none'}
                    />
                  </Field>
                </div>

                <div className="mt-6">
                  <label
                    data-testid="contacto-acepta-legal-label"
                    className={`flex items-start gap-3 text-xs text-brand-muted cursor-pointer select-none ${errors.acepta_legal ? 'text-[#E74C3C]' : ''}`}
                  >
                    <input
                      type="checkbox"
                      data-testid="contacto-acepta-legal"
                      checked={data.acepta_legal}
                      onChange={onChange('acepta_legal')}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-brand-violet"
                    />
                    <span>
                      He leído y acepto la{' '}
                      <button
                        type="button"
                        data-testid="contacto-link-privacidad"
                        onClick={() => onOpenLegal && onOpenLegal('privacidad')}
                        className="underline font-semibold text-brand-violet"
                      >
                        Política de Privacidad
                      </button>
                      {' '}y los{' '}
                      <button
                        type="button"
                        data-testid="contacto-link-terminos"
                        onClick={() => onOpenLegal && onOpenLegal('terminos')}
                        className="underline font-semibold text-brand-violet"
                      >
                        Términos y Condiciones
                      </button>
                      . Autorizo a PreventiApp a contactarme por los datos entregados.
                    </span>
                  </label>
                  {errors.acepta_legal && (
                    <p className="mt-1 text-xs font-semibold text-[#E74C3C]" data-testid="contacto-acepta-legal-error">
                      {errors.acepta_legal}
                    </p>
                  )}
                </div>

                {serverError && (
                  <div
                    data-testid="contacto-server-error"
                    className="mt-4 rounded-xl border border-[#E74C3C]/40 bg-[#E74C3C]/5 text-[#B93A2A] text-sm px-4 py-3"
                    role="alert"
                  >
                    {serverError}
                  </div>
                )}

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-brand-muted max-w-sm">
                    No compartimos tu información con terceros ajenos al servicio.
                  </p>
                  <button
                    type="submit"
                    data-testid="contacto-submit"
                    disabled={sending}
                    className="btn-primary px-6 py-3.5 rounded-full font-semibold text-base inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {sending ? 'Enviando...' : (<>Enviar mensaje <Send className="w-4 h-4" /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const inputCls = (err) =>
  `w-full rounded-xl border px-4 py-3 text-base text-brand-deep bg-white outline-none transition-all placeholder:text-[#9AA6C5] focus:ring-2 focus:ring-brand-violet/20 ${
    err ? 'border-[#E74C3C]' : 'border-brand focus:border-brand-violet'
  }`;

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-deep/70">{label}</span>
    <div className="mt-1.5">{children}</div>
    {error && <span className="block mt-1.5 text-xs font-semibold text-[#E74C3C]">{error}</span>}
  </label>
);

export default Contacto;
